import { Menu } from "@mindlercare/ui-web";
import { type Dispatch, type SetStateAction, useState } from "react";
import { styled } from "styled-components";

import type { Language } from "../types";

type DropdownItem = {
  label: string;
  value: number;
};

export type LanguageDropdownProps = {
  selectedLanguage: Language;
  selectLanguage: Dispatch<SetStateAction<Language>>;
  languages: Language[];
  translateLanguageCode?: (code: string) => string;
};

export const LanguageDropdown = ({
  selectedLanguage,
  selectLanguage,
  languages,
  translateLanguageCode,
}: LanguageDropdownProps) => {
  const getLabel = (language: Language) =>
    translateLanguageCode
      ? translateLanguageCode(language.codeISO3.toUpperCase())
      : language.name;

  const allLanguages = languages.map((language) => ({
    value: language.languageId,
    label: getLabel(language),
  }));

  const [dropdownLanguage, setDropdownLanguage] = useState<DropdownItem>({
    value: selectedLanguage.languageId,
    label: getLabel(selectedLanguage),
  });

  const setDropdownLanguageOnChange = (languageOption: DropdownItem) => {
    const language = languages.find(
      (l) => l.languageId === languageOption.value,
    );
    if (!language) return;
    selectLanguage(language);
    setDropdownLanguage(languageOption);
  };

  if (allLanguages.length <= 1) return null;

  return (
    <Menu>
      <Trigger variant="secondary" withExpandIcon>
        {dropdownLanguage.label}
      </Trigger>
      <Menu.Items>
        {allLanguages.map((lang) => (
          <Menu.Item
            onClick={() => setDropdownLanguageOnChange(lang)}
            key={lang.label}
          >
            {lang.label}
          </Menu.Item>
        ))}
      </Menu.Items>
    </Menu>
  );
};

const Trigger = styled(Menu.Trigger)`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.SPACING.XS};
`;
